import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import { Typography, Card, CardContent, FormControl, InputLabel, Select, MenuItem, IconButton, Stack } from "@mui/material";
import { Grid } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useMaterialUIController } from "context";
import { puedeAccion } from "lib/seguridad/permisosPorModulo";
import { useNavigate } from "react-router-dom";
import { FP, PageHeader, StatCard, fpDataGridSx, fpInputSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";

function Informegastos() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  const puedeBorrar = puedeAccion(roleDetalles, "gastos", "borrar");
  const navigate = useNavigate();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");
  const [nosucursal, setNosucursal] = useState(null);
  const sucursal = useSucursales();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [estado, setEstado] = useState("activos"); // activos | cancelados
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gastoTarget, setGastoTarget] = useState(null);

  async function procesarRequerimiento(estadoOverride) {
    if (!fecha1 || !fecha2) {
      alert("Selecciona un rango de fechas");
      return;
    }
    const est = estadoOverride !== undefined ? estadoOverride : estado;
    const sucCode = nosucursal?.label ? String(nosucursal.label).split("-")[0] : "todo";
    const { status, data } = await post("/api/consultargastos", {
      fechadesde: fecha1,
      fechahasta: fecha2,
      sucursal: sucCode || "todo",
      estado: est,
      tipodeusuario: "",
    });
    if (status === 200) {
      const list = (data["data"] || []).map((r, i) => ({ ...r, id: r.id ?? `row-${i}` }));
      setRows(list);
      const monto = data?.total?.monto;
      const num = parseFloat(String(monto || "0").replace(/,/g, ""));
      setTotal(Number.isNaN(num) ? 0 : num);
    }
    if (status === 400) {
      setRows([]);
      setTotal(0);
      alert(data?.Error || "Sin datos");
    }
  }

  function onEstadoChange(value) {
    setEstado(value);
    setRows([]);
    setTotal(0);
    if (fecha1 && fecha2) procesarRequerimiento(value);
  }

  function abrirConfirmacion(row) {
    setGastoTarget(row);
    setConfirmOpen(true);
  }

  async function eliminarGasto() {
    if (!gastoTarget) return;
    try {
      const { status, data } = await post("/api/eliminargasto", { id: gastoTarget.id });
      if (status === 200) {
        const valorNum = parseFloat(String(gastoTarget.valor || "0").replace(/,/g, ""));
        setRows((prev) => prev.filter((r) => r.id !== gastoTarget.id));
        if (!Number.isNaN(valorNum)) setTotal((prev) => Math.max(0, prev - valorNum));
        setConfirmOpen(false);
        setGastoTarget(null);
      } else {
        alert(data?.Error || "No se pudo eliminar el gasto");
        setConfirmOpen(false);
      }
    } catch (err) {
      alert("Error de red: " + err.message);
      setConfirmOpen(false);
    }
  }

  const fmtMoney = (v) =>
    Number(parseFloat(String(v || "0").replace(/,/g, ""))).toLocaleString("es-DO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80, align: "center", headerAlign: "center" },
      { field: "fecha", headerName: "Fecha", width: 120, align: "center", headerAlign: "center" },
      { field: "tipogastos", headerName: "Categoría", flex: 1, minWidth: 150 },
      { field: "descripcion", headerName: "Descripción", flex: 2, minWidth: 220 },
      { field: "sucursal", headerName: "Sucursal", width: 110, align: "center", headerAlign: "center" },
      {
        field: "valor",
        headerName: "Valor",
        type: "number",
        width: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => `$${fmtMoney(value)}`,
      },
      ...(puedeBorrar
        ? [
            {
              field: "acciones",
              headerName: "Acciones",
              width: 100,
              sortable: false,
              filterable: false,
              align: "center",
              headerAlign: "center",
              renderCell: (params) => {
                if (estado === "cancelados") return null;
                return (
                  <IconButton
                    size="small"
                    onClick={() => abrirConfirmacion(params.row)}
                    sx={{
                      color: FP.color.danger,
                      "&:hover": { bgcolor: FP.color.dangerSoft },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                );
              },
            },
          ]
        : []),
    ],
    [estado, puedeBorrar]
  );

  const isCancelados = estado === "cancelados";

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Gastos"
        subtitle={isCancelados ? "Gastos Cancelados" : "Informe de Gastos"}
        cta={
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate("/gastos/agregar")}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
            }}
          >
            Agregar gasto
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        {/* Filtros */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
            <Typography sx={{ ml: "auto", color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>
              Rango · Sucursal · Estado
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(e) => setFecha1(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(e) => setFecha2(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal || null}
                  onChange={(e, value) => setNosucursal(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="estado-label" shrink>
                    Estado
                  </InputLabel>
                  <Select
                    labelId="estado-label"
                    label="Estado"
                    notched
                    value={estado}
                    onChange={(e) => onEstadoChange(e.target.value)}
                  >
                    <MenuItem value="activos">Activos</MenuItem>
                    <MenuItem value="cancelados">Cancelados</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarRequerimiento()}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                }}
              >
                Procesar
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Stats */}
        {rows.length > 0 && (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
            }}
          >
            <StatCard
              label="Total de registros"
              value={String(rows.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            <StatCard
              label={isCancelados ? "Total cancelados" : "Total de gastos"}
              value={`$${fmtMoney(total)}`}
              icon={isCancelados ? CancelOutlinedIcon : TrendingDownOutlinedIcon}
              accent={isCancelados ? FP.color.danger : FP.color.warning}
            />
          </Box>
        )}

        {/* DataGrid */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: isCancelados ? FP.color.dangerSoft : FP.color.warningSoft,
                color: isCancelados ? FP.color.danger : FP.color.warning,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isCancelados ? <CancelOutlinedIcon sx={{ fontSize: 18 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              {isCancelados ? "Gastos cancelados" : "Gastos activos"}
            </Typography>
            <Typography sx={{ ml: "auto", color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>
              {rows.length} {rows.length === 1 ? "registro" : "registros"}
            </Typography>
          </Box>
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <Box sx={{ height: 520 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                disableSelectionOnClick
                experimentalFeatures={{ newEditingApi: true }}
                rowHeight={44}
                sx={fpDataGridSx}
              />
            </Box>
          </Box>
        </Card>

        {/* Confirm Dialog */}
        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2.5, boxShadow: FP.shadow.md },
          }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary }}>Eliminar gasto</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: FP.color.textSecondary }}>
              ¿Seguro que deseas eliminar este gasto?
            </DialogContentText>
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: FP.color.surfaceAlt, borderRadius: 1.5, border: `1px solid ${FP.color.border}` }}>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.95rem" }}>
                {gastoTarget?.tipogastos || "—"}
              </Typography>
              {gastoTarget?.descripcion ? (
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.85rem", mt: 0.5 }}>
                  {gastoTarget.descripcion}
                </Typography>
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              sx={{ textTransform: "none", color: FP.color.textSecondary, fontWeight: 700 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={eliminarGasto}
              sx={{
                bgcolor: FP.color.danger,
                color: "#FFFFFF",
                borderRadius: 1.4,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { bgcolor: "#BE123C", boxShadow: "none" },
              }}
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default Informegastos;
